"use client";

import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useSession } from "next-auth/react";
import { useSearchParams } from "next/navigation";

type FormState = {
  name: string;
  email: string;
  role: string;
  category: string;
  message: string;
};

const initialForm: FormState = {
  name: "",
  email: "",
  role: "",
  category: "",
  message: "",
};

export default function ContactPage() {
  const { data: session } = useSession();
  const searchParams = useSearchParams();

  const [form, setForm] = useState<FormState>(initialForm);
  const [sourcePage, setSourcePage] = useState("/contact");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const user = session?.user as any;

    if (!user) return;

    setForm((current) => ({
      ...current,
      name: current.name || user.name || "",
      email: current.email || user.email || "",
      role: current.role || user.role || "",
    }));
  }, [session]);

  useEffect(() => {
    const from = searchParams.get("from");

    if (from && from.trim()) {
      setSourcePage(from.trim());
    } else {
      setSourcePage("/contact");
    }
  }, [searchParams]);

  function updateField(
    field: keyof FormState,
    value: string
  ) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    setError("");
    setSubmitting(true);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...form,
          sourcePage,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.message || "Unable to send your question."
        );
      }

      setSubmitted(true);

      setForm((current) => ({
        ...initialForm,
        name: current.name,
        email: current.email,
        role: current.role,
      }));
    } catch (err: any) {
      console.error("[contact] submit failed:", err);

      setError(
        err?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <main className="min-h-[calc(100vh-70px)] bg-slate-950 text-white">
        <div className="mx-auto max-w-3xl px-4 py-16">
          <div className="rounded-2xl border border-green-500/30 bg-green-500/10 p-8 text-center shadow-xl">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-green-500/20 text-2xl">
              ✓
            </div>

            <h1 className="text-3xl font-black">
              Your question has been sent
            </h1>

            <p className="mx-auto mt-4 max-w-xl text-slate-300">
              Thanks for reaching out to Ready Roster. Your message has
              been received and we’ll get back to you as soon as possible.
            </p>

            <button
              type="button"
              onClick={() => setSubmitted(false)}
              className="mt-7 rounded-xl bg-white px-5 py-3 text-sm font-bold text-slate-950 transition hover:bg-slate-200"
            >
              Send another question
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-[calc(100vh-70px)] bg-slate-950 text-white">
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <div className="mb-8">
          <p className="mb-2 text-sm font-bold uppercase tracking-[0.2em] text-red-400">
            Ready Roster Support
          </p>

          <h1 className="text-4xl font-black tracking-tight sm:text-5xl">
            Have a question?
          </h1>

          <p className="mt-4 max-w-2xl text-slate-300">
            Whether you need help finding a wrestler, joining a team,
            managing a match, or using Ready Roster, send us a message
            below.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl sm:p-8"
        >
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <label
                htmlFor="name"
                className="mb-2 block text-sm font-bold text-slate-200"
              >
                Name
              </label>

              <input
                id="name"
                type="text"
                required
                maxLength={120}
                value={form.name}
                onChange={(e) =>
                  updateField("name", e.target.value)
                }
                placeholder="Your name"
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-white outline-none transition placeholder:text-slate-600 focus:border-red-500"
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="mb-2 block text-sm font-bold text-slate-200"
              >
                Email
              </label>

              <input
                id="email"
                type="email"
                required
                maxLength={255}
                value={form.email}
                onChange={(e) =>
                  updateField("email", e.target.value)
                }
                placeholder="you@example.com"
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-white outline-none transition placeholder:text-slate-600 focus:border-red-500"
              />
            </div>
          </div>

          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            <div>
              <label
                htmlFor="role"
                className="mb-2 block text-sm font-bold text-slate-200"
              >
                I am a...
              </label>

              <select
                id="role"
                required
                value={form.role}
                onChange={(e) =>
                  updateField("role", e.target.value)
                }
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-white outline-none transition focus:border-red-500"
              >
                <option value="">Select one</option>
                <option value="Parent">Parent</option>
                <option value="Coach">Coach</option>
                <option value="Athlete">Athlete</option>
                <option value="Event Director">
                  Event Director
                </option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="category"
                className="mb-2 block text-sm font-bold text-slate-200"
              >
                What can we help with?
              </label>

              <select
                id="category"
                required
                value={form.category}
                onChange={(e) =>
                  updateField("category", e.target.value)
                }
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-white outline-none transition focus:border-red-500"
              >
                <option value="">Choose a topic</option>

                <option value="Account / Login">
                  Account / Login
                </option>

                <option value="Finding a Team">
                  Finding a Team
                </option>

                <option value="Finding an Athlete">
                  Finding an Athlete
                </option>

                <option value="Match / Invitation">
                  Match / Invitation
                </option>

                <option value="Event Question">
                  Event Question
                </option>

                <option value="How Ready Roster Works">
                  How Ready Roster Works
                </option>

                <option value="Bug / Technical Issue">
                  Bug / Technical Issue
                </option>

                <option value="Feature Suggestion">
                  Feature Suggestion
                </option>

                <option value="Other">
                  Other
                </option>
              </select>
            </div>
          </div>

          <div className="mt-6">
            <label
              htmlFor="message"
              className="mb-2 block text-sm font-bold text-slate-200"
            >
              Your question
            </label>

            <textarea
              id="message"
              required
              rows={7}
              maxLength={5000}
              value={form.message}
              onChange={(e) =>
                updateField("message", e.target.value)
              }
              placeholder="Tell us what you're trying to do or what you're having trouble with..."
              className="w-full resize-none rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-white outline-none transition placeholder:text-slate-600 focus:border-red-500"
            />
          </div>

          {error && (
            <div className="mt-5 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {error}
            </div>
          )}

          <div className="mt-7 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-slate-500">
              We’ll use this information to help answer your
              Ready Roster question.
            </p>

            <button
              type="submit"
              disabled={submitting}
              className="rounded-xl bg-gradient-to-b from-[#ff3b3b] to-[#e31d2d] px-6 py-3 font-bold text-white shadow-lg transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
            >
              {submitting
                ? "Sending..."
                : "Submit Question"}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}